import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Skeleton,
  Stack,
  TableCell,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router';
import { useExportAllCustomMatchesMutation } from 'src/services/tournaments';
import { t } from 'i18next';
import CustomSelect from 'src/components/forms/theme-elements/CustomSelect';
import moment from 'moment';
import { ScheduleMatch } from 'src/services/tournaments/getTournamentSchedule';

import MatchItem from './components/MatchItem';
import EditMatch from './components/EditMatch';
import useToast from 'src/hooks/useToast';
import { tournamentDateComparator } from './helpers/getTournamentProgress';
import { useSelector } from 'react-redux';
import { AppState } from 'src/store/Store';
import useTournament from '../../hooks/useTournament';
import CreateExtraMatch from './components/CreateExtraMatch';

const ExtraMatches = ({
  isLoading,
  AdditionalTournaments,
}: {
  isLoading: boolean;
  AdditionalTournaments: () => JSX.Element;
}) => {
  const { id } = useParams();

  const [exportAllMatchesApi] = useExportAllCustomMatchesMutation();

  const { toastPromise } = useToast();

  const schedule = useSelector((state: AppState) => state.tournamentReducer.tournamentSchedule);

  const { isScheduleLoading, loadTournamentSchedule } = useTournament();

  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState(0);
  const [openEditMatch, setOpenEditMatch] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<ScheduleMatch | undefined>(undefined);
  const [openCreateExtraMatches, setOpenCreateExtraMatches] = useState(false);
  //filters
  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    t('TournamentScheduleTab.allDates'),
  );
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(
    t('TournamentScheduleTab.allStatus'),
  );
  const [selectedLocation, setSelectedLocation] = useState<string | undefined>(
    t('TournamentScheduleTab.allLocations'),
  );

  useEffect(() => {
    if (schedule) {
      const categoriesOutput = Object.keys(schedule?.data);
      setCategories(categoriesOutput);
      setSelectedCategory(categoriesOutput[0]);
    }
  }, [schedule]);

  const data = useMemo(() => {
    return schedule && selectedCategory ? schedule?.data?.[selectedCategory] : null;
  }, [schedule, selectedCategory]);

  const sectionsData = useMemo(() => {
    if (!data?.tournament_match_groups?.[selectedGroup]?.tournament_custom_matches) return [];
    const dataOfGroup: ScheduleMatch[] = [
      ...data.tournament_match_groups[selectedGroup].tournament_custom_matches,
    ];

    const liveMatches: ScheduleMatch[] = [];
    const upcomingMatches: ScheduleMatch[] = [];
    const pastMatches: ScheduleMatch[] = [];

    const finalData: (ScheduleMatch | string)[] = [];

    dataOfGroup.forEach((item: ScheduleMatch) => {
      const dateTime = moment(
        `${item.match_date} ${item.match_time}`,
        'YYYY-MM-DD hh:mm:ss',
      ).unix();
      item = { ...item, dateTime };

      if (item.is_live && !item.winner_team) {
        liveMatches.push(item);
      } else if (!item.is_live && !item.winner_team) {
        upcomingMatches.push(item);
      } else if (item.winner_team) {
        pastMatches.push(item);
      }
    });

    liveMatches.sort((a, b) => a.dateTime - b.dateTime);
    upcomingMatches.sort((a, b) => a.dateTime - b.dateTime);
    pastMatches.sort((a, b) => a.dateTime - b.dateTime);

    if (liveMatches?.length) {
      finalData.push(t('TournamentScheduleTab.live'), ...liveMatches);
    }
    if (upcomingMatches?.length) {
      finalData.push(t('TournamentScheduleTab.upcoming'), ...upcomingMatches);
    }
    if (pastMatches?.length) {
      finalData.push(t('TournamentScheduleTab.past'), ...pastMatches);
    }

    return finalData;
  }, [data, selectedCategory, selectedGroup]);

  const dates = useMemo(() => {
    const dates = [t('TournamentScheduleTab.allDates')];

    const allMatches = data?.tournament_match_groups?.[selectedGroup]?.tournament_custom_matches;

    allMatches?.map((match) => dates.push(match.match_date));

    const uniqueDates = [...new Set(dates)];

    return uniqueDates
      ?.sort((a, b) => tournamentDateComparator(a, b))
      .filter((date) => date !== null);
  }, [data, selectedGroup]);

  const filteredData = useMemo(() => {
    const filteredData = sectionsData?.filter((item) => {
      if (typeof item === 'string') return false;

      if (selectedDate === t('TournamentScheduleTab.allDates')) return true;

      return item.match_date === selectedDate;
    });

    let liveMatches: ScheduleMatch[] = [];
    let upcomingMatches: ScheduleMatch[] = [];
    let pastMatches: ScheduleMatch[] = [];

    filteredData.forEach((item: ScheduleMatch) => {
      const dateTime = moment(
        `${item.match_date} ${item.match_time}`,
        'YYYY-MM-DD hh:mm:ss',
      ).unix();
      item = { ...item, dateTime };

      if (item.is_live && !item.winner_team) {
        liveMatches.push(item);
      } else if (!item.is_live && !item.winner_team) {
        upcomingMatches.push(item);
      } else if (item.winner_team) {
        pastMatches.push(item);
      }
    });

    liveMatches.sort((a, b) => a.dateTime - b.dateTime);
    upcomingMatches.sort((a, b) => a.dateTime - b.dateTime);
    pastMatches.sort((a, b) => a.dateTime - b.dateTime);

    const finalData: (ScheduleMatch | string)[] = [];

    if (selectedLocation !== t('TournamentScheduleTab.allLocations')) {
      liveMatches = liveMatches.filter((item) => item.pitch_name === selectedLocation);
      upcomingMatches = upcomingMatches.filter((item) => item.pitch_name === selectedLocation);
      pastMatches = pastMatches.filter((item) => item.pitch_name === selectedLocation);
    }

    if (selectedStatus === t('TournamentScheduleTab.allStatus')) {
      if (liveMatches?.length) {
        finalData.push(t('TournamentScheduleTab.live'), ...liveMatches);
      }
      if (upcomingMatches?.length) {
        finalData.push(t('TournamentScheduleTab.upcoming'), ...upcomingMatches);
      }
      if (pastMatches?.length) {
        finalData.push(t('TournamentScheduleTab.past'), ...pastMatches);
      }
    } else {
      if (selectedStatus === t('TournamentScheduleTab.live')) {
        finalData.push(...liveMatches);
      } else if (selectedStatus === t('TournamentScheduleTab.upcoming')) {
        finalData.push(...upcomingMatches);
      } else if (selectedStatus === t('TournamentScheduleTab.past')) {
        finalData.push(...pastMatches);
      }
    }

    return finalData;
  }, [sectionsData, selectedDate, selectedStatus, selectedLocation]);

  const exportAllMatches = () => {
    toastPromise({
      request: () => exportAllMatchesApi({ tournamentId: Number(id) }),
      onSuccess: (result) => {
        if ('error' in result) {
          throw result.error;
        }

        //download the file
        const link = document.createElement('a');
        link.href = result?.data?.data?.url;
        link.download = `schedule_${id}.xlsx`;
        link.click();

        return t('TournamentScheduleTab.scheduleExportedSuccessfully');
      },
      onError: (err) => {
        if (err.data && err.data.errors) {
          const errorMessages = Object.values(err.data.errors).flat().join('. ');

          return errorMessages;
        }

        return t('TournamentScheduleTab.scheduleExportFailed');
      },
    });
  };

  const createExtraMatches = () => {
    setOpenCreateExtraMatches(true);
  };

  const renderGroup = (item: any, index: number) => {
    return (
      <MenuItem key={index} value={index} onClick={() => setSelectedGroup(index)}>
        {item.is_single_elimination
          ? t('TournamentScheduleTab.knockout')
          : t('TournamentScheduleTab.group', { value: index + 1 })}
      </MenuItem>
    );
  };

  const Row = ({ row }: { row: ScheduleMatch }) => {
    return (
      <MatchItem
        row={row}
        onEdit={() => {
          setSelectedMatch(row);
          setOpenEditMatch(true);
        }}
        loadMatches={loadTournamentSchedule}
      />
    );
  };

  const renderPicker = () => {
    if (AdditionalTournaments) {
      return <AdditionalTournaments />;
    }

    if (categories?.length > 0) {
      return (
        <CustomSelect
          id="category_id"
          // fullWidth
          onChange={(e: any) => setSelectedCategory(e.target.value)}
          value={selectedCategory}
          sx={{
            marginRight: 1,
          }}
        >
          {categories?.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </CustomSelect>
      );
    }

    return <></>;
  };

  const HeaderToolbar = () => (
    <Toolbar style={{ padding: 0 }}>
      <Box sx={{ flex: '1 1 100%' }}>
        <Stack direction="row" spacing={2}>
          <Button color="primary" variant="contained" onClick={createExtraMatches} sx={{ mr: 0 }}>
            {t('TournamentScheduleTab.createExtraMatches')}
          </Button>
        </Stack>
      </Box>

      {isLoading || isScheduleLoading ? (
        <Skeleton sx={{ width: 250 }} variant="text" />
      ) : (
        <Stack direction="row" spacing={1}>
          <Button
            color="error"
            variant="contained"
            onClick={exportAllMatches}
            sx={{ mr: 0, width: 155 }}
          >
            {t('TournamentScheduleTab.exportAllMatches')}
          </Button>
        </Stack>
      )}

      {data?.tournament_match_groups?.length > 1 && (
        <CustomSelect
          id="group_id"
          onChange={(e: any) => setSelectedGroup(e.target.value)}
          value={selectedGroup}
        >
          {data?.tournament_match_groups?.map(renderGroup)}
        </CustomSelect>
      )}
    </Toolbar>
  );

  if (isLoading || isScheduleLoading)
    return (
      <Box display={'flex'} justifyContent={'center'} alignItems={'center'} width={'100%'}>
        <CircularProgress color="secondary" sx={{ m: 5 }} />
      </Box>
    );

  return (
    <Grid display={'flex'} flexDirection={'column'} flex={1}>
      <Box m={0} flexGrow={1}>
        <HeaderToolbar />
        <Divider sx={{ mb: 0 }} />
      </Box>
      <Stack py={1} flexDirection={'row'} justifyContent={'space-between'}>
        <Stack direction={'row'} spacing={1}>
          {renderPicker()}
          <CustomSelect
            id="location_id"
            onChange={(e: any) => setSelectedLocation(e.target.value)}
            value={selectedLocation}
            sx={{
              width: 150,
              mx: 0,
            }}
          >
            <MenuItem value={t('TournamentScheduleTab.allLocations')}>
              {t('TournamentScheduleTab.allLocations')}
            </MenuItem>
            {sectionsData?.map((item) => {
              if (typeof item === 'string') return null;

              if (!item.pitch_name) return null;

              return (
                <MenuItem key={item.pitch_name} value={item.pitch_name}>
                  {item.pitch_name}
                </MenuItem>
              );
            })}
          </CustomSelect>
        </Stack>
        <Stack direction={'row'} spacing={1} justifyContent={'flex-end'}>
          <CustomSelect
            id="status_id"
            onChange={(e: any) => setSelectedStatus(e.target.value)}
            value={selectedStatus}
            sx={{
              width: 120,
              mx: 0,
            }}
          >
            <MenuItem value={t('TournamentScheduleTab.allStatus')}>
              {t('TournamentScheduleTab.allStatus')}
            </MenuItem>
            {sectionsData?.map((item) => {
              if (typeof item !== 'string') return null;

              return (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              );
            })}
          </CustomSelect>
          <CustomSelect
            id="date_id"
            key={selectedDate}
            value={selectedDate}
            onChange={(e: any) => setSelectedDate(e.target.value)}
            placeholder={t('TournamentScheduleTab.selectDate')}
            sx={{
              width: 155,
            }}
          >
            {dates?.map((date) => (
              <MenuItem key={date} value={date}>
                {date}
              </MenuItem>
            ))}
          </CustomSelect>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 0 }} />

      {filteredData?.length > 0 ? (
        <Grid flexDirection={'row'} display={'flex'} flexWrap={'wrap'} style={{ padding: 0 }}>
          {filteredData?.map((item, index) => {
            if (typeof item === 'string') {
              return (
                <TableRow key={index} style={{ width: '100%', marginTop: 10, marginBottom: 10 }}>
                  <TableCell style={{ padding: 0, margin: 10, paddingBottom: 15 }}>
                    <Typography variant="h6">{item}</Typography>
                  </TableCell>
                </TableRow>
              );
            }

            return <Row key={item.id} row={item} />;
          })}
        </Grid>
      ) : (
        <Typography m={5} textAlign={'center'} variant="h6">
          {t('TournamentStandingScreen.standingEmptyState')}
        </Typography>
      )}

      <EditMatch
        open={openEditMatch}
        onClose={() => {
          setSelectedMatch(undefined);
          setOpenEditMatch(false);
        }}
        selectedMatch={selectedMatch}
        loadMatches={loadTournamentSchedule}
      />
      <CreateExtraMatch
        open={openCreateExtraMatches}
        onClose={() => setOpenCreateExtraMatches(false)}
        loadMatches={loadTournamentSchedule}
      />
    </Grid>
  );
};

export default ExtraMatches;
